import { useMemo } from 'react';

import dayjs from 'dayjs';

import { Icons, Tooltip, Typography, Box, Stack } from '@cast/design-system';
import { Cluster } from '@cast/types';
import { CloudVolume } from '@cast/types-stop';

import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { StorageDiskCharts } from '../_components/StorageDiskCharts';
import {
  NodeStorageMetricsStep,
  useNodeStorageMetricsQuery,
} from 'hooks/queries/nodes/useNodeStorageMetricsQuery';

import { useNodeStorageTimeRangeSelection } from '../_hooks/useNodeStorageTimeRangeSelection';

const SYNC_ID = 'root-disk-charts';

type RootDiskSectionProps = {
  node?: NodeWithDependencies;
  cluster: Cluster;
  rootDisk?: CloudVolume;
  isNodeLoading: boolean;
  isCloudDisksEnabled?: boolean;
};

export const RootDiskSection = ({
  node,
  cluster,
  rootDisk,
  isCloudDisksEnabled,
  isNodeLoading,
}: RootDiskSectionProps) => {
  const { getRange, key, timeZone } = useNodeStorageTimeRangeSelection();

  const { startTime, endTime, step } = useMemo(() => {
    const { from, to } = getRange();

    const hourDiff = dayjs(to).diff(dayjs(from), 'hours');
    const step: NodeStorageMetricsStep =
      hourDiff >= 48 ? '1h' : hourDiff >= 5 ? '5m' : '1m';

    return { startTime: from, endTime: to, step };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const { series, summary, isLoading, error, refetch } =
    useNodeStorageMetricsQuery({
      clusterId: cluster.id,
      nodeName: node?.name,
      storageType: 'root',
      startTime,
      endTime,
      step,
    });

  return (
    <Box data-testid="root-disk-section">
      <Stack direction="row" alignItems="center" gap={6} sx={{ mb: 8 }}>
        <Stack direction="row" alignItems="center" gap={4}>
          <Typography variant="h6">Root disk</Typography>
          <Tooltip
            title={
              <Box>
                <div>Volume ID: {rootDisk?.volumeId || '-'}</div>
                <div>Type: {rootDisk?.volumeType || '-'}</div>
                <div>CSP: {rootDisk?.cloudProvider?.toUpperCase() || '-'}</div>
                <div>Zone: {rootDisk?.zone || '-'}</div>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box
              component="span"
              sx={{ display: 'inline-flex', cursor: 'help', color: 'grey.700' }}
            >
              <Icons.Info size={12} />
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      <StorageDiskCharts
        series={series}
        summary={{
          usedBytes: summary?.usedBytes?.avg,
          provisionedSizeBytes: summary?.provisionedSizeBytes?.avg,
          throughputBytes: summary?.throughputBytes?.avg,
          iops: summary?.iops?.avg,
          provisionedIops: summary?.provisionedIops?.avg,
          provisionedThroughputBytes: summary?.provisionedThroughputBytes?.avg,
        }}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        syncId={SYNC_ID}
        timeZone={timeZone}
        isNodeReady={!isNodeLoading}
        isCloudDisksEnabled={isCloudDisksEnabled}
        labels={{
          capacity: {
            label: 'Fixed',
            tooltip:
              'Root disk capacity is set at node creation and does not change automatically.',
          },
        }}
      />
    </Box>
  );
};
